import { Topbar } from "components/ui/shared/Topbar";
import { Button } from "components/ui/controls/Button";
import { HeaderInput } from 'components/ui/inputs/HeaderInput';
import { Tab, TabsGroup } from 'components/ui/controls/Tabs';
import { ProjectsContext } from "providers/ProjectsProvider";
import { TasksContext } from "providers/TasksProvider";
import { useState, useContext, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { ProjectGeneralInfo } from '../../components/features/projects/ProjectGeneralInfo';
import { useApi } from 'providers/UserProvider';
import { ProjectActivity } from 'components/features/projects/ProjectActivity';
import { ProjectContent } from 'components/features/projects/ProjectContent';
import { ProjectMap } from 'components/features/projects/ProjectMap';
import { ProjectDeliveries } from 'components/features/projects/ProjectDeliveries';
import { ProjectPhases } from 'components/features/projects/ProjectPhases';
import { ProjectTasks } from 'components/features/projects/ProjectTasks';
import { useErrors } from 'providers/ErrorsProvider';
import { ProjectNotes } from 'components/features/projects/ProjectNotes';
import { MaterialIcon } from "components/ui/shared/MaterialIcon";

export const ProjectSettings = () => {

    const navigate = useNavigate();

    const { api } = useApi();
    const { setError, unsetError } = useErrors();

    const { projectUuid, taskUuid } = useParams();
    const { projects, projectsLoading, handleProjectEdit } = useContext(ProjectsContext);
    const { tasks, tasksLoading } = useContext(TasksContext);
    const [ activeTab, setActiveTab ] = useState("generalInfo");

    const [ projectSettings, setProjectSettings ] = useState(
        {
            projectUuid: null,
            projectNumber: "",
            projectName: "",
            projectFolder: "",
        }
    );

    const [ projectActivitySettings, setProjectActivitySettings ] = useState({
        enabled: false,
        displayOnDashboard: false,
        automaticStatusChange: false,
        useLastActivityAsFuture: false,
        useToCalculateAvailability: false,
        activity: {}
    });
    const [ projectContentSettings, setProjectContentSettings ] = useState({
        enabled: false,
        road: false,
        sign: false,
        traffic: false,
        signal: false,
        safety: false,
        document: false,
        models3D: false,
        bim: false
    });
    const [ projectMapSettings, setProjectMapSettings ] = useState({
        enabled: false,
        position: null,
        county: null,
        municipality: null,
        ntmZone: null,
        displayOnDashboard: false
    });
    const [ projectNotesSettings, setProjectNotesSettings ] = useState({enabled: false});
    const [ projectTasksSettings, setProjectTasksSettings ] = useState({enabled: false});
    const [ projectDeliveriesSettings, setProjectDeliveriesSettings ] = useState({
        enabled: false,
        deliveries: []
    });
    const [ projectPhasesSettings, setProjectPhasesSettings ] = useState({
        enabled: false,
        mainPhase: null,
        paths: [],
    });
    const [ projectVersionsSettings, setProjectVersionsSettings ] = useState({enabled: false});

    const saveFunction = async () => {

        let changesValidation = {
            projectName: true,
            projectNumber: true,
        }
        
        if(projectSettings.projectName.length < 5)
        {
            setError("PROJECT_NAME_TOO_SHORT");
            unsetError("PROJECT_NAME_TOO_LONG");
            changesValidation.projectName = false;
            
        }
        else if(projectSettings.projectName.length > 50)
        {
            setError("PROJECT_NAME_TOO_LONG");
            unsetError("PROJECT_NAME_TOO_SHORT");
            changesValidation.projectName = false;
        }
        else
        {
            unsetError("PROJECT_NAME_TOO_LONG");
            unsetError("PROJECT_NAME_TOO_SHORT");
        }

        if(projectSettings.projectNumber.length < 3)
        {
            setError("PROJECT_NUMBER_TOO_SHORT");
            unsetError("PROJECT_NUMBER_TOO_LONG");
            changesValidation.projectNumber = false;
        }
        else if(projectSettings.projectNumber.length > 10)
        {
            setError("PROJECT_NUMBER_TOO_LONG");
            unsetError("PROJECT_NUMBER_TOO_SHORT");
            changesValidation.projectNumber = false;
        }
        else
        {
            unsetError("PROJECT_NUMBER_TOO_LONG");
            unsetError("PROJECT_NUMBER_TOO_SHORT");
        }

        if(!Object.values(changesValidation).every(Boolean)) return;

        if(projectSettings.projectUuid)
        {
            handleProjectEdit({
                projectUuid: projectSettings.projectUuid,
                action: "EDIT PROJECT",
                changes:
                {
                    projectNumber: projectSettings.projectNumber,
                    projectName: projectSettings.projectName,
                    projectFolder: projectSettings.projectFolder,
                    projectDescription: projectSettings.projectDescription,

                    projectActivity: projectActivitySettings,
                    projectContent: projectContentSettings,
                    projectMap: projectMapSettings,
                    projectNotes: projectNotesSettings,
                    projectTasks: projectTasksSettings,
                    projectDeliveries: projectDeliveriesSettings,
                    projectPhases: projectPhasesSettings,
                    projectVersions: projectVersionsSettings
                }
            }).then(res => {
                if(res?.status) setError(res.reason);
            });

            
        }
        else
        {   
            handleProjectEdit({
                projectUuid: crypto.randomUUID(),
                action: "CREATE PROJECT",
                changes:
                {
                    projectNumber: projectSettings.projectNumber,
                    projectName: projectSettings.projectName,
                    projectFolder: projectSettings.projectFolder,
                    projectDescription: projectSettings.projectDescription,

                    projectActivity: projectActivitySettings,
                    projectContent: projectContentSettings,
                    projectMap: projectMapSettings,
                    projectNotes: projectNotesSettings,
                    projectTasks: projectTasksSettings,
                    projectDeliveries: projectDeliveriesSettings,
                    projectPhases: projectPhasesSettings,
                    projectVersions: projectVersionsSettings
                }
            }).then(res => {
                if(res?.status) setError(res.reason);
            });
        }
        
        navigate('/projects');
        return;
    }

    const discardFunction = () => {

        unsetError("PROJECT_NAME_TOO_SHORT");
        unsetError("PROJECT_NAME_TOO_LONG");
        unsetError("PROJECT_NUMBER_TOO_LONG");
        unsetError("PROJECT_NUMBER_TOO_SHORT");

        navigate("/projects");
        return;
    }

    useEffect(() => {
        if (projectsLoading) return;
        if (!projects?.length) return;
        if (!projectUuid || projectUuid === 'new') return;

        const project = projects.find(prj => prj.projectUuid === projectUuid);

        if (!project)
        {
            navigate("/projects/new");
            return;
        }
        else
        {
            api.request("projects/query/description", {projectUuid: project.projectUuid})
            .then(desc => {
                if(!desc) return;
                setProjectSettings({
                    projectUuid: project.projectUuid,
                    projectNumber: project.projectNumber,
                    projectName: project.projectName,
                    projectFolder: project.projectFolder,
                    projectDescription: desc.projectDescription
                });

                setProjectActivitySettings(project.projectActivity);
                setProjectContentSettings(project.projectContent);
                setProjectMapSettings(project.projectMap);
                setProjectNotesSettings(project.projectNotes);
                setProjectTasksSettings(project.projectTasks);
                setProjectDeliveriesSettings(project.projectDeliveries);
                setProjectPhasesSettings(project.projectPhases);
                setProjectVersionsSettings(project.projectVersions);

            })
        }
    }, [projectsLoading, projects, projectUuid, tasksLoading, tasks, taskUuid, navigate, api]);

    return (
        <>
            <Topbar breacrumb={['Prosjekter', projectUuid === 'new' || !projectUuid ? "Ny prosjekt" : 'Prosjektinnstillinger']}>
                <Button
                    type='secondary'
                    text="Forkast"
                    onClick={() => discardFunction()}
                />
                <Button
                    type='primary'
                    text="Lagre"
                    prefix={<MaterialIcon name='save' weight={400}/>}
                    onClick={() => saveFunction()}
                />
            </Topbar>
            <StyledProjectSettingsWrapper>
                <StyledProjectSettingsContainer>
                    <HeaderInput
                        value={projectSettings.projectName}
                        name=''
                        label='Skriv inn prosjektnavn.'
                        placeholder='Prosjektnavn...'
                        paddingLeft='xxl'
                        paddingRight='xxl'
                        textsize='xl'
                        onChange={(e) => setProjectSettings(prev => ({...prev, projectName: e}))}
                    />

                    <TabsGroup
                        onChange={(e) => setActiveTab(e)}
                        paddingLeft='xxl'
                        paddingRight='xxl'
                    >
                        <Tab name="Generell informasjon" value="generalInfo" isSelected={activeTab === 'generalInfo'}/>
                        {projectActivitySettings.enabled ? <Tab name="Prosjektaktivitet" value="projectActivity" isSelected={activeTab === 'projectActivity'}/> : null}
                        {projectContentSettings.enabled ? <Tab name="Innhold" value="projectContent" isSelected={activeTab === 'projectContent'}/> : null}
                        {projectMapSettings.enabled ? <Tab name="Kart" value="projectMap" isSelected={activeTab === 'projectMap'}/> : null}
                        {projectNotesSettings.enabled ? <Tab name="Notater" value="projectNotes" isSelected={activeTab === 'projectNotes'}/> : null}
                        {projectTasksSettings.enabled ? <Tab name="Oppgaver" value="projectTasks" isSelected={activeTab === 'projectTasks'}/> : null}
                        {projectDeliveriesSettings.enabled ? <Tab name="Leveranser" value="projectDeliveries" isSelected={activeTab === 'projectDeliveries'}/> : null}
                        {projectPhasesSettings.enabled ? <Tab name="Prosjektfaser" value="projectPhases" isSelected={activeTab === 'projectPhases'}/> : null}
                        {projectVersionsSettings.enabled ? <Tab name="Versjonslogg" value="projectVersions" isSelected={activeTab === 'projectVersions'}/> : null}
                    </TabsGroup>
                    <div className='projectSettingsContent'>
                        {
                            activeTab === 'generalInfo' ?   <ProjectGeneralInfo
                                                                content={{
                                                                    projectNumber: projectSettings.projectNumber,
                                                                    projectFolder: projectSettings.projectFolder,
                                                                    projectDescription: projectSettings.projectDescription
                                                                }}
                                                                handler={setProjectSettings}
                                                                projectActivity={{enabled: projectActivitySettings.enabled, handler: setProjectActivitySettings}}
                                                                projectContent={{enabled: projectContentSettings.enabled, handler: setProjectContentSettings}}
                                                                projectMap={{enabled: projectMapSettings.enabled, handler: setProjectMapSettings}}
                                                                projectNotes={{enabled: projectNotesSettings.enabled, handler: setProjectNotesSettings}}
                                                                projectTasks={{enabled: projectTasksSettings.enabled, handler: setProjectTasksSettings}}
                                                                projectDeliveries={{enabled: projectDeliveriesSettings.enabled, handler: setProjectDeliveriesSettings}}
                                                                projectPhases={{enabled: projectPhasesSettings.enabled, handler: setProjectPhasesSettings}}
                                                                projectVersions={{
                                                                    enabled: projectVersionsSettings.enabled,
                                                                    handler: setProjectVersionsSettings,
                                                                    isAvailable: projectMapSettings.enabled && projectMapSettings.ntmZone ? true : false,
                                                                }}
                                                            /> :
                            activeTab === 'projectActivity' ? <ProjectActivity content={projectActivitySettings} handler={setProjectActivitySettings}/> :
                            activeTab === 'projectContent' ? <ProjectContent content={projectContentSettings} handler={setProjectContentSettings}/> :
                            activeTab === 'projectMap' ? <ProjectMap content={projectMapSettings} handler={setProjectMapSettings}/> :
                            activeTab === 'projectNotes' ? <ProjectNotes content={projectNotesSettings} handler={setProjectNotesSettings} projectUuid={projectUuid}/>:
                            activeTab === 'projectTasks' ? <ProjectTasks projectUuid={projectUuid}/> :
                            activeTab === 'projectDeliveries' ? <ProjectDeliveries projectUuid={projectUuid} content={projectDeliveriesSettings} handler={setProjectDeliveriesSettings}/> :
                            activeTab === 'projectPhases' ? <ProjectPhases content={projectPhasesSettings} handler={setProjectPhasesSettings}/> :
                            activeTab === 'projectVersions' ? "Versjonslogg" : null
                        }
                    </div>
                </StyledProjectSettingsContainer>
            </StyledProjectSettingsWrapper>
        </>
    )
}

const StyledProjectSettingsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1 1 auto;
    overflow: hidden;
`

const StyledProjectSettingsContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 1200px;
    overflow: hidden;

    flex: 1 1 auto;
    gap: ${({theme}) => theme.spacing.xl};

    div.projectSettingsContent
    {
        flex: 1 1 auto;
        overflow: hidden;

        padding-left: ${({theme}) => theme.spacing.xxl};
        padding-right: ${({theme}) => theme.spacing.xxl};

        > div
        {
            overflow: auto;
        }
    }
`